import { useStyletron } from '@thanx/uikit/theme'
import React, { useCallback, useEffect, useRef } from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface MultiSelectProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether the dropdown menu is open */
  isOpen?: boolean
  /** Called when user clicks outside or presses Escape (to close the dropdown) */
  onClose?: () => void
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled MultiSelect component.
 *
 * Composable wrapper for MultiSelectTrigger, MultiSelectMenu, MultiSelectHeader,
 * and MultiSelectOption. Handles click-outside and Escape key to close.
 * All visual state determined by props.
 */
const MultiSelect = React.memo(
  ({
    isOpen = false,
    onClose,
    children,
    style,
    className,
    ...props
  }: MultiSelectProps) => {
    const [css] = useStyletron()
    const containerRef = useRef<HTMLDivElement>(null)

    // Handle click outside
    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        if (
          isOpen &&
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          onClose?.()
        }
      },
      [isOpen, onClose]
    )

    // Handle escape key
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (isOpen && event.key === 'Escape') {
          onClose?.()
        }
      },
      [isOpen, onClose]
    )

    // Event listener cleanup
    useEffect(() => {
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
          document.removeEventListener('mousedown', handleClickOutside)
          document.removeEventListener('keydown', handleKeyDown)
        }
      }
      return undefined
    }, [isOpen, handleClickOutside, handleKeyDown])

    const containerClassName = css(getContainerStyles())

    return (
      <div
        ref={containerRef}
        data-open={isOpen}
        className={`${containerClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const getContainerStyles = () => ({
  position: 'relative' as const,
  display: 'block',
})

export default MultiSelect
